import { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Home as HomeIcon, Sparkles, Users, MapPin } from "lucide-react";

/** Rutas antiguas o mal escritas y su destino real. */
const SUGGESTIONS: Record<string, { to: string; labelKey: string }> = {
  "/app": { to: "/#app", labelKey: "notFound.appLabel" },
  "/descargar": { to: "/#app", labelKey: "notFound.appLabel" },
  "/pwa": { to: "/#app", labelKey: "notFound.appLabel" },
  "/ritual": { to: "/#app", labelKey: "notFound.appLabel" },
  "/terapeutas": { to: "/#terapeutas", labelKey: "notFound.therapistsLabel" },
  "/buscar": { to: "/#terapeutas", labelKey: "notFound.therapistsLabel" },
  "/profesionales": { to: "/#terapeutas", labelKey: "notFound.therapistsLabel" },
  "/terapeuta": { to: "/#terapeutas", labelKey: "notFound.therapistsLabel" },
  "/retiros": { to: "/retiro", labelKey: "notFound.retreatLabel" },
  "/residencia": { to: "/retiro", labelKey: "notFound.retreatLabel" },
  "/residency": { to: "/retiro", labelKey: "notFound.retreatLabel" },
  "/terminos-y-condiciones": { to: "/terminos", labelKey: "notFound.termsLabel" },
  "/terms": { to: "/terminos", labelKey: "notFound.termsLabel" },
  "/politica-de-privacidad": { to: "/privacidad", labelKey: "notFound.privacyLabel" },
  "/privacy": { to: "/privacidad", labelKey: "notFound.privacyLabel" },
};

const LINKS = [
  { to: "/", labelKey: "notFound.homeLabel", descKey: "notFound.homeDesc", icon: HomeIcon },
  { to: "/#terapeutas", labelKey: "notFound.therapistsLabel", descKey: "notFound.therapistsDesc", icon: Users },
  { to: "/#app", labelKey: "notFound.appLabelShort", descKey: "notFound.appDesc", icon: Sparkles },
  { to: "/retiro", labelKey: "notFound.retreatLabelShort", descKey: "notFound.retreatDesc", icon: MapPin },
];

const NotFound = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const suggestion = useMemo(() => {
    const path = location.pathname.replace(/\/+$/, "").toLowerCase() || "/";
    if (SUGGESTIONS[path]) return SUGGESTIONS[path];
    const firstSegment = "/" + path.split("/")[1];
    return SUGGESTIONS[firstSegment] ?? null;
  }, [location.pathname]);

  useEffect(() => {
    const referrer = typeof document !== "undefined" ? document.referrer : "";
    const internal =
      typeof window !== "undefined" &&
      !!referrer &&
      referrer.startsWith(window.location.origin);

    console.warn("[404] Ruta no encontrada", {
      ruta: location.pathname,
      query: location.search || null,
      referrer: referrer || null,
      origenInterno: internal,
      sugerencia: suggestion?.to ?? null,
      hora: new Date().toISOString(),
    });
  }, [location.pathname, location.search, suggestion]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title={t("notFound.seoTitle")}
        description={t("notFound.seoDescription")}
        url="https://masshappy.com/404"
        noindex
      />

      <main className="flex-1 pt-28 pb-20">
        <div className="container mx-auto max-w-3xl px-4 sm:px-6">
          <p className="text-[11px] uppercase tracking-[0.35em] text-muted-foreground font-medium">
            {t("notFound.error")}
          </p>

          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-semibold text-foreground leading-[1.1] tracking-tight">
            {t("notFound.title")}
          </h1>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
            {t("notFound.lead")}
          </p>

          <p className="mt-3 text-sm text-muted-foreground/80 break-all">
            {t("notFound.searched")} <span className="font-mono text-foreground/70">{location.pathname}</span>
          </p>

          {suggestion && (
            <div className="mt-8 rounded-2xl border border-primary/25 bg-primary/5 p-5 sm:p-6">
              <p className="text-sm text-muted-foreground">{t("notFound.maybe")}</p>
              <Button asChild size="lg" className="mt-3">
                <Link to={suggestion.to}>
                  {t(suggestion.labelKey)}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}

          <div className="mt-10 grid gap-3 sm:grid-cols-2">
            {LINKS.map(({ to, labelKey, descKey, icon: Icon }) => (
              <Link
                key={labelKey}
                to={to}
                className="group flex items-start gap-4 rounded-2xl border border-border/60 bg-card p-5 shadow-soft transition-smooth hover:border-primary/40"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-semibold text-foreground">{t(labelKey)}</span>
                  <span className="block text-sm text-muted-foreground">{t(descKey)}</span>
                </span>
              </Link>
            ))}
          </div>

          <p className="mt-8 text-sm text-muted-foreground">
            {t("notFound.legalPrefix")}{" "}
            <Link to="/terminos" className="text-primary hover:underline">
              {t("notFound.legalTerms")}
            </Link>{" "}
            {t("notFound.legalMiddle")}{" "}
            <Link to="/privacidad" className="text-primary hover:underline">
              {t("notFound.legalPrivacy")}
            </Link>
            .
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;
